import { createContext, useContext, useState, useRef, useCallback } from 'react'

const BuilderContext = createContext()

export const useBuilder = () => {
  const context = useContext(BuilderContext)
  if (!context) {
    throw new Error('useBuilder must be used within BuilderProvider')
  }
  return context
}

const DEFAULT_BODY_SETTINGS = {
  emailWidth: 600,
  backgroundColor: '#f4f4f4',
  contentBackgroundColor: '#ffffff',
  fontFamily: 'Arial, sans-serif',
  linkColor: '#007bff',
  contentPadding: 0,
  contentBorderRadius: '0px',
  preheaderText: ''
}

const MAX_HISTORY = 50

export const BuilderProvider = ({ children }) => {
  const [blocks, setBlocks] = useState([])
  const [selectedBlockId, setSelectedBlockId] = useState(null)
  const [subject, setSubject] = useState('')
  const [bodySettings, setBodySettings] = useState({ ...DEFAULT_BODY_SETTINGS })

  // Undo/Redo history
  const historyRef = useRef([])
  const historyIndexRef = useRef(-1)
  const isUndoRedoRef = useRef(false)

  const pushHistory = useCallback((newBlocks) => {
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false
      return
    }
    // Truncate forward history
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1)
    historyRef.current.push(JSON.stringify(newBlocks))
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.shift()
    }
    historyIndexRef.current = historyRef.current.length - 1
  }, [])

  const setBlocksWithHistory = useCallback((newBlocksOrFn) => {
    setBlocks(prev => {
      const newBlocks = typeof newBlocksOrFn === 'function' ? newBlocksOrFn(prev) : newBlocksOrFn
      pushHistory(newBlocks)
      return newBlocks
    })
  }, [pushHistory])

  const undo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1
      isUndoRedoRef.current = true
      const prevState = JSON.parse(historyRef.current[historyIndexRef.current])
      setBlocks(prevState)
    }
  }, [])

  const redo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current += 1
      isUndoRedoRef.current = true
      const nextState = JSON.parse(historyRef.current[historyIndexRef.current])
      setBlocks(nextState)
    }
  }, [])

  const canUndo = historyIndexRef.current > 0
  const canRedo = historyIndexRef.current < historyRef.current.length - 1

  const addBlock = (block) => {
    setBlocksWithHistory(prev => [...prev, block])
    setSelectedBlockId(block.id)
  }

  const updateBlock = (blockId, updates) => {
    setBlocksWithHistory(prev => prev.map(block => {
      if (block.id === blockId) {
        return {
          ...block,
          settings: { ...block.settings, ...updates }
        }
      }

      // Update block inside columns
      if (block.type === 'columns' && block.settings.columns) {
        const updatedColumns = block.settings.columns.map(col => {
          if (col.blocks) {
            const updatedBlocks = col.blocks.map(nestedBlock => {
              if (nestedBlock.id === blockId) {
                return {
                  ...nestedBlock,
                  settings: { ...nestedBlock.settings, ...updates }
                }
              }
              return nestedBlock
            })

            if (updatedBlocks !== col.blocks) {
              return { ...col, blocks: updatedBlocks }
            }
          }
          return col
        })

        if (updatedColumns !== block.settings.columns) {
          return {
            ...block,
            settings: { ...block.settings, columns: updatedColumns }
          }
        }
      }

      return block
    }))
  }

  const deleteBlock = (blockId) => {
    setBlocksWithHistory(prev => prev.filter(block => block.id !== blockId))
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null)
    }
  }

  const duplicateBlock = (blockId) => {
    setBlocks(prev => {
      const blockToDuplicate = prev.find(b => b.id === blockId)
      if (!blockToDuplicate) return prev
      const newBlock = {
        ...JSON.parse(JSON.stringify(blockToDuplicate)),
        id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }
      const index = prev.findIndex(b => b.id === blockId)
      const newBlocks = [...prev]
      newBlocks.splice(index + 1, 0, newBlock)
      pushHistory(newBlocks)
      setSelectedBlockId(newBlock.id)
      return newBlocks
    })
  }

  const moveBlock = (dragIndex, hoverIndex) => {
    setBlocksWithHistory(prev => {
      const dragBlock = prev[dragIndex]
      const newBlocks = [...prev]
      newBlocks.splice(dragIndex, 1)
      newBlocks.splice(hoverIndex, 0, dragBlock)
      return newBlocks
    })
  }

  const moveBlockUp = (blockId) => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.id === blockId)
      if (idx <= 0) return prev
      const newBlocks = [...prev]
      ;[newBlocks[idx - 1], newBlocks[idx]] = [newBlocks[idx], newBlocks[idx - 1]]
      pushHistory(newBlocks)
      return newBlocks
    })
  }

  const moveBlockDown = (blockId) => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.id === blockId)
      if (idx === -1 || idx >= prev.length - 1) return prev
      const newBlocks = [...prev]
      ;[newBlocks[idx], newBlocks[idx + 1]] = [newBlocks[idx + 1], newBlocks[idx]]
      pushHistory(newBlocks)
      return newBlocks
    })
  }

  const getSelectedBlock = () => {
    const topLevel = blocks.find(block => block.id === selectedBlockId)
    if (topLevel) return topLevel

    for (const block of blocks) {
      if (block.type === 'columns' && block.settings.columns) {
        for (const col of block.settings.columns) {
          if (col.blocks) {
            const nested = col.blocks.find(b => b.id === selectedBlockId)
            if (nested) return nested
          }
        }
      }
    }

    return null
  }

  const updateBodySettings = (updates) => {
    setBodySettings(prev => ({ ...prev, ...updates }))
  }

  const clearAll = () => {
    setBlocksWithHistory([])
    setSelectedBlockId(null)
    setSubject('')
  }

  const loadTemplate = (templateData) => {
    const newBlocks = templateData.blocks || []
    setBlocks(newBlocks)
    pushHistory(newBlocks)
    setSubject(templateData.subject || '')
    setSelectedBlockId(null)
    if (templateData.bodySettings) {
      setBodySettings(prev => ({ ...prev, ...templateData.bodySettings }))
    }
  }

  const value = {
    blocks,
    setBlocks: setBlocksWithHistory,
    selectedBlockId,
    setSelectedBlockId,
    subject,
    setSubject,
    bodySettings,
    updateBodySettings,
    addBlock,
    updateBlock,
    deleteBlock,
    duplicateBlock,
    moveBlock,
    moveBlockUp,
    moveBlockDown,
    getSelectedBlock,
    clearAll,
    loadTemplate,
    undo,
    redo,
    canUndo,
    canRedo
  }

  return (
    <BuilderContext.Provider value={value}>
      {children}
    </BuilderContext.Provider>
  )
}
